import axiosClient from './axiosClient';

type TokenGetter = () => Promise<string>;

let tokenGetter: TokenGetter | null = null;

/**
 * Register the request interceptor exactly once at module load. The token
 * getter is swapped at runtime via `setTokenGetter`, so any API call made
 * during the very first render (before Auth0's `useEffect` has run) goes
 * through the interceptor and simply omits the header until the getter is
 * set — the response interceptor + ProtectedRoute handle the unauth case.
 *
 * This side-stepped bug: previously the interceptor lived inside a
 * `useEffect` in `AuthTokenAttacher`, which runs AFTER the first render.
 * Any query fired during that first render went out without a bearer
 * token, 401'd, and the response interceptor redirected the user to `/`.
 */
axiosClient.interceptors.request.use(async (config) => {
  const getter = tokenGetter;
  if (!getter) return config;
  try {
    const token = await getter();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (err) {
    // Common paths: login_required, consent_required, invalid_grant
    // (refresh-token reuse), network failure. Let the request proceed
    // unauthenticated; the response interceptor / ProtectedRoute will
    // surface the auth issue to the user.
    // eslint-disable-next-line no-console
    console.warn('[authInterceptor] failed to obtain token:', err);
  }
  return config;
});

export const setTokenGetter = (getter: TokenGetter | null): void => {
  tokenGetter = getter;
};
